"use client";

import { c as _c } from "react/compiler-runtime";
import NextImage from "next/image";
import type { FC, MouseEventHandler, ReactNode } from "react";
import { useEffect, useState } from "react";
import { FaSearchPlus } from "react-icons/fa";
import { Modal, ModalBody, ModalHeader } from "@/components/modal";
import type { ComponentPropsWithoutChildren } from "@/lib/component-props";
import { cn } from "@/lib/utils";
import type { StoryblokImageDimensions } from "@/storyblok/image-dimensions";
export interface MediaLightboxProps extends ComponentPropsWithoutChildren<"button"> {
  children: ReactNode;
  src: string;
  alt: string;
  caption?: string;
  imageDimensions: StoryblokImageDimensions | null;
}
export const MediaLightbox: FC<MediaLightboxProps> = t0 => {
  const $ = _c(42);
  let alt;
  let caption;
  let children;
  let className;
  let imageDimensions;
  let onClick;
  let props;
  let src;
  if ($[0] !== t0) {
    ({
      children,
      src,
      alt,
      caption,
      imageDimensions,
      className,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = caption;
    $[3] = children;
    $[4] = className;
    $[5] = imageDimensions;
    $[6] = onClick;
    $[7] = props;
    $[8] = src;
  } else {
    alt = $[1];
    caption = $[2];
    children = $[3];
    className = $[4];
    imageDimensions = $[5];
    onClick = $[6];
    props = $[7];
    src = $[8];
  }
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  let t1;
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsMounted(true);
    };
    t2 = [];
    $[9] = t1;
    $[10] = t2;
  } else {
    t1 = $[9];
    t2 = $[10];
  }
  useEffect(t1, t2);
  let t3;
  if ($[11] !== onClick) {
    t3 = event => {
      onClick?.(event);
      if (!event.defaultPrevented) {
        setIsOpen(true);
      }
    };
    $[11] = onClick;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleOpen = t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsOpen(false);
    };
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleClose = t4;
  const t5 = isMounted ? "relative" : undefined;
  let t6;
  if ($[14] !== className || $[15] !== t5) {
    t6 = cn("block w-full cursor-zoom-in text-left", t5, className);
    $[14] = className;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = `Open image in lightbox: ${alt}`;
  let t8;
  if ($[17] !== isMounted) {
    t8 = isMounted ? <span aria-hidden={true} className="pointer-events-none absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-md border-2 border-black bg-yellow-300 px-2 py-1 text-[10px] font-black uppercase tracking-wide text-black shadow-[2px_2px_0_0]"><FaSearchPlus className="size-3" /><span>Zoom</span></span> : null;
    $[17] = isMounted;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== children || $[20] !== handleOpen || $[21] !== props || $[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = <button type="button" className={t6} aria-haspopup="dialog" aria-label={t7} onClick={handleOpen} {...props}>{children}{t8}</button>;
    $[19] = children;
    $[20] = handleOpen;
    $[21] = props;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ModalHeader onClose={handleClose}>Image Preview</ModalHeader>;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== alt || $[28] !== imageDimensions || $[29] !== src) {
    t11 = imageDimensions ? <NextImage src={src} alt={alt} width={imageDimensions.width} height={imageDimensions.height} sizes="90vw" className="mx-auto h-auto max-h-[80dvh] w-auto max-w-full object-contain" /> : <img src={src} alt={alt} className="mx-auto max-h-[80dvh] w-auto max-w-full object-contain" />;
    $[27] = alt;
    $[28] = imageDimensions;
    $[29] = src;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== caption) {
    t12 = caption ? <p className="mt-3 text-center text-sm font-medium text-white">{caption}</p> : null;
    $[31] = caption;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t11 || $[34] !== t12) {
    t13 = <ModalBody className="bg-black p-3 sm:p-4">{t11}{t12}</ModalBody>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== isOpen || $[37] !== t13) {
    t14 = <Modal open={isOpen} onClose={handleClose} className="max-w-8xl">{t10}{t13}</Modal>;
    $[36] = isOpen;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t14 || $[40] !== t9) {
    t15 = <>{t9}{t14}</>;
    $[39] = t14;
    $[40] = t9;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  return t15;
};